import React from 'react';
import { Star, Quote } from 'lucide-react';
import { useInView } from 'react-intersection-observer';

// Função para gerar cor consistente a partir do nome
const stringToColor = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash % 360);
  return `hsl(${hue}, 70%, 50%)`;
};

// Componente Avatar com iniciais
const Avatar = ({ name }) => {
  const initials = name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase();

  return (
    <div
      className='w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg'
      style={{ backgroundColor: stringToColor(name) }}
    >
      {initials}
    </div>
  );
};

const Testimonials = () => {
  const testimonials = [
    {
      name: 'Ellem T.',
      role: 'Usuária',
      rating: 5,
      text: 'Finalmente um app intuitivo e que simplifica nossa vida. A organização é simples e direta, aquele simples bem feito e que os diversos apps pagos e caros que testei não entregaram. Interface limpa e sem burocracia para quem quer controle de verdade. Parabéns ao desenvolvedor, nota 10!',
    },
    {
      name: 'Igor L.',
      role: 'Usuário',
      rating: 5,
      text: 'Muito bom. Já testei vários aplicativos, e sempre sentia falta de alguma função, inclusive no momento estou com um pago, que não transfere saldo de meses anteriores e o Finziin faz, superou o que venho usando, estou trocando tudo pra cá.',
    },
    {
      name: 'Paula C.',
      role: 'Usuária',
      rating: 5,
      text: 'Melhor App que já usei! O App é intuitivo, fácil de configurar e muito bonito, além de ser gratuito.',
    },
    {
      name: 'Osmar F.',
      role: 'Usuário',
      rating: 5,
      text: 'O aplicativo maravilhoso,tem como você filtrar lançamentos,criar contas e cadastrar Cartão Crédito e além disso fácil manuseio e com layout maravilhoso,obrigado a toda equipe desenvolveu esse aplicativo ☺️☺️🤗🤗',
    },
    {
      name: 'Jefferson A.',
      role: 'Usuário',
      rating: 5,
      text: 'Como esse app tem tão poucos downloads? Esse é o melhor, mais prático, mais acessível app dentre todos que já testei. É SURREAL! Esse app eu resumo em apenas 3 palavras: IMPRESSIONANTE e GRÁTIS!',
    },
    {
      name: 'Chris A.',
      role: 'Usuário',
      rating: 5,
      text: 'Excelente app , super interativo e bem elaborado, muito útil no dia a dia , super recomendado! Parabéns ao desenvolvedor.',
    },
  ];

  const stats = [
    { value: '4.8⭐', label: 'Avaliação média na Play Store' },
    { value: '500+', label: 'Usuários ativos mensalmente' },
    { value: '98%', label: 'Recomendariam a um amigo' },
  ];

  const [refHeader, inViewHeader] = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });
  const [refStats, inViewStats] = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });
  const [refTestimonials, inViewTestimonials] = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  return (
    <section id='depoimentos' className='py-40 bg-gray-50'>
      <div className='container mx-auto px-4'>
        {/* Header */}
        <div className='text-center mb-16' ref={refHeader}>
          <h2
            className={`text-4xl lg:text-5xl font-bold mb-6 transition-transform duration-700 ${
              inViewHeader
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 -translate-y-10'
            }`}
          >
            O que nossos usuários
            <span className='gradient-text block'>estão dizendo</span>
          </h2>
          <p
            className={`text-xl text-gray-600 max-w-3xl mx-auto transition-opacity duration-700 ${
              inViewHeader ? 'opacity-100' : 'opacity-0'
            }`}
          >
            Histórias reais de quem conquistou controle, clareza e tranquilidade
            financeira com o FinZiin.
          </p>
        </div>

        {/* Stats Row */}
        <div
          className='grid grid-cols-3 md:grid-cols-3 gap-8 mb-16'
          ref={refStats}
        >
          {stats.map((stat, i) => (
            <div
              key={i}
              className={`text-center transition-all duration-700 ${
                inViewStats
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-6'
              }`}
              style={{ transitionDelay: `${i * 200}ms` }}
            >
              <div className='text-4xl font-bold gradient-text mb-2'>
                {stat.value}
              </div>
              <div className='text-gray-600'>{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Testimonials Grid */}
        <div
          className='grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16'
          ref={refTestimonials}
        >
          {testimonials.map((t, i) => (
            <div
              key={i}
              className={`bg-white rounded-2xl p-6 shadow-lg transition-transform duration-500 transform hover:scale-105 hover:shadow-2xl ${
                inViewTestimonials
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${i * 200}ms` }}
            >
              <div className='flex justify-between items-start mb-4'>
                <Quote className='w-8 h-8 text-blue-500 opacity-60' />
                <div className='flex space-x-1'>
                  {[...Array(t.rating)].map((_, j) => (
                    <Star
                      key={j}
                      className='w-4 h-4 text-yellow-400 fill-current'
                    />
                  ))}
                </div>
              </div>
              <p className='text-gray-700 mb-6 leading-relaxed'>"{t.text}"</p>
              <div className='flex items-center space-x-3'>
                <Avatar name={t.name} />
                <div>
                  <div className='font-semibold text-gray-800'>{t.name}</div>
                  <div className='text-sm text-gray-600'>{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className='text-center mt-16'>
          <h3 className='text-2xl font-bold mb-4 text-gray-800'>
            Junte-se a centenas de usuários satisfeitos
          </h3>
          <p className='text-gray-600 mb-6'>
            Baixe agora e veja como é simples organizar suas finanças com o
            FinZiin.
          </p>
          <a
            href='https://play.google.com/store/apps/details?id=br.android.finziin'
            target='_blank'
            rel='noopener noreferrer'
            className='btn-primary inline-flex items-center space-x-3 text-lg'
          >
            <Star className='w-5 h-5' />
            <span>Baixar grátis</span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
